<script>
import Breadcrumbs from '@/components/file-browser/Breadcrumbs';
import FileList from '@/components/file-list/FileList';
import ImageList from '@/components/image-list/ImageList';
import URI from 'urijs';
import debounce from 'lodash/debounce';
import getErrorMessage from '@/modules/server-errors';
import { basename, dirname, join, removeBasePath, safeFilename } from '@/modules/file-utilities';

export default {
  name: 'FileBrowser',
  components: {
    Breadcrumbs,
    FileList,
    ImageList
  },
  props: {
    basePath: {
      type: String,
      default: ''
    },
    canSelect: {
      type: Function,
      default: () => true
    },
    multiple: {
      type: Boolean,
      default: false
    },
    showSelect: {
      type: Boolean,
      default: true
    },
    showPages: {
      type: Boolean,
      default: false
    },
    showUnsplash: {
      type: Boolean,
      default: false
    },
    site: {
      type: Object,
      required: true
    },
    type: {
      default: 'inline',
      validator(type) {
        return ['inline', 'dialog', 'popover'].includes(type);
      }
    }
  },
  data() {
    return {
      activeTab: 'pages',
      currentPath: '',
      isCreateFolderFormOpen: false,
      isCreatingFolder: false,
      isDeleteFormOpen: false,
      isDeleting: false,
      isLoadingUnsplash: false,
      isLoadingUploads: false,
      isLoadingPages: false,
      isOpen: false,
      isRenameFormOpen: false,
      isRenaming: false,
      isUploading: false,
      newFilename: '',
      newFolderName: '',
      pageFilter: '',
      pages: [],
      pageSelection: [],
      unsplashCurrentPage: 1,
      unsplashCanLoadMore: false,
      unsplashFilter: '',
      unsplashImages: [],
      unsplashOrientation: '',
      unsplashSelection: [],
      uploads: [],
      uploadSelection: []
    };
  },
  methods: {
    open(options) {
      options = Object.assign(
        {
          path: ''
        },
        options
      );
      this.uploads = [];
      this.pages = [];
      this.pageFilter = '';
      this.pageSelection = [];
      this.unsplashImages = [];
      this.unsplashFilter = '';
      this.unsplashSelection = [];
      this.uploads = [];
      this.uploadSelection = [];
      this.isOpen = true;
      this.$emit('open');

      // The active tab can't be a disabled tab
      if (this.activeTab === 'pages' && !this.showPages) this.activeTab = 'uploads';
      if (this.activeTab === 'unsplash' && !this.showUnsplash) this.activeTab = 'uploads';

      // Focus on the appropriate input
      this.$nextTick(() => {
        if (this.activeTab === 'pages') this.$refs.pageFilter.focus();
        if (this.activeTab === 'unsplash') this.$refs.unsplashFilter.focus();
      });

      // Fetch pages and uploads
      if (this.showPages) this.getPages(0);
      this.getUploads(options.path);
    },

    close() {
      this.isOpen = false;
      this.isCreateFolderFormOpen = false;
      this.isDeleteFormOpen = false;
      this.isRenameFormOpen = false;
      this.$emit('close');
    },

    getPages(offset) {
      const limit = 100;
      offset = offset || 0;

      this.isLoadingPages = true;
      return this.$http
        .get(`/sites/${this.site.id}/pages`, {
          params: {
            offset,
            limit,
            sortBy: 'name'
          }
        })
        .then(res => {
          if (offset === 0) {
            this.pages = res.data.pages;
            this.$refs.pageList.setSelection([]);
          } else {
            this.pages = this.pages.concat(res.data.pages);
          }

          // Continue fetching if more pages are available
          if (res.data.count > offset + limit) {
            return this.getPages(offset + limit);
          }
        })
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => (this.isLoadingPages = false));
    },

    // Fetches images from the Unsplash API
    getUnsplashImages: debounce(function (startOver) {
      if (this.isLoadingUnsplash) return;
      if (!startOver && !this.unsplashCanLoadMore) return;
      const page = startOver ? 1 : this.unsplashCurrentPage + 1;
      const search = this.unsplashFilter;
      const orientation = this.unsplashOrientation;
      const perPage = 30; // current API limit

      if (this.unsplashFilter) {
        this.isLoadingUnsplash = true;
        this.$http
          .get('/utilities/unsplash', {
            params: { search, page, perPage, orientation }
          })
          .then(res => {
            const results = res.data.results.map(image =>
              Object.assign(image, {
                src: image.urls.thumb,
                alt: image.description,
                infoLink: image.links.html + '?utm_source=Surreal%20CMS&utm_medium=referral'
              })
            );
            this.unsplashCurrentPage = page;
            this.unsplashCanLoadMore = page < res.data.total_pages;
            if (startOver) {
              this.$refs.unsplashList.scrollToTop();
              this.unsplashImages = results;
            } else {
              this.unsplashImages = this.unsplashImages.concat(results);
            }
          })
          .catch(err => {
            this.$notify({
              type: 'error',
              title: this.$t('Error'),
              message: getErrorMessage(err.response.data.code)
            });
          })
          .finally(() => (this.isLoadingUnsplash = false));
      } else {
        this.unsplashCurrentPage = 1;
        this.unsplashImages = [];
      }
    }, 500),

    getUploads(path) {
      // Make sure the path is inside the upload folder
      this.currentPath = this.isUnderBasePath(path) ? path : this.basePath;
      this.isLoadingUploads = true;
      this.$emit('path-change', this.currentPath);
      this.$http
        .get(`/sites/${this.site.id}/files`, {
          params: { path: this.currentPath }
        })
        .then(res => {
          this.uploads = res.data.files
            .map(file => {
              return Object.assign(file, { isDisabled: !this.canSelect(file) });
            })
            .filter(file => {
              // Don't show the parent directory if we're at the base path
              return this.currentPath === this.basePath ? file.name !== '..' : true;
            });
          this.$refs.uploadList.setSelection([]);
          this.$refs.uploadList.scrollToTop();
        })
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => (this.isLoadingUploads = false));
    },

    handleCreateFolder() {
      const path = join(this.currentPath, this.newFolderName);
      this.isCreateFolderFormOpen = false;

      // If the directory already exists, just select it
      const dir = this.uploads.filter(file => file.path === path);
      if (dir.length) {
        this.$refs.uploadList.setSelection(dir);
        this.$nextTick(() => this.$refs.uploadList.scrollToSelection());
        return;
      }

      this.isCreatingFolder = true;
      this.$http
        .post(`/sites/${this.site.id}/files`, { path })
        .then(() => {
          const directory = {
            type: 'directory',
            path,
            name: basename(path),
            extension: '',
            size: 0
          };
          this.uploads = this.sortFiles(this.uploads.concat([directory]));
          this.$refs.uploadList.setSelection(directory);
          this.$nextTick(() => this.$refs.uploadList.scrollToSelection());
        })
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => (this.isCreatingFolder = false));
    },

    handleDelete() {
      const files = this.uploadSelection;
      this.isDeleting = true;
      this.isDeleteFormOpen = false;
      const paths = files.map(file => file.path);
      if (paths.length) {
        this.$http
          .delete(`/sites/${this.site.id}/files`, {
            data: { paths }
          })
          .then(res => {
            const deletedPaths = res.data.files.map(file => file.path);
            this.uploads = this.uploads.filter(file => !deletedPaths.includes(file.path));
          })
          .catch(err => {
            this.$notify({
              type: 'error',
              title: this.$t('Error'),
              message: getErrorMessage(err.response.data.code)
            });
          })
          .finally(() => (this.isDeleting = false));
      }
    },

    handleRename() {
      const file = this.uploadSelection[0];
      const path = file.path;
      const existingPaths = this.uploads.map(file => file.path);
      const newPath = join(dirname(path), this.newFilename);
      this.isRenameFormOpen = false;

      // Check for an existing file
      if (existingPaths.includes(newPath)) {
        this.$notify({
          type: 'error',
          title: this.$t('Error'),
          message: this.$t('A file already exists at the specified location.')
        });
        return;
      }

      this.isRenaming = true;
      this.$http
        .put(`/sites/${this.site.id}/files`, { files: [{ path, newPath }] })
        .then(res => {
          const file = res.data.files[0];
          const files = this.uploads.filter(file => file.path !== path);
          if (file) {
            this.uploads = this.sortFiles(files.concat(file));
            this.$refs.uploadList.setSelection(file);
            this.$nextTick(() => this.$refs.uploadList.scrollToSelection());
          }
        })
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => (this.isRenaming = false));
    },

    handleNewFilenameChange() {
      this.newFilename = safeFilename(this.newFilename);
    },

    handleNewFolderNameChange() {
      this.newFolderName = safeFilename(this.newFolderName);
    },

    handlePageSelect() {
      const selection = this.pageSelection;
      if (selection.length) {
        this.$emit('select', 'page', selection);
        this.close();
      }
    },

    handleUploadSelect() {
      const selection = this.uploadSelection;
      if (selection.length) {
        this.$emit('select', 'upload', selection);
        this.close();
      }
    },

    handleUnsplashSelect() {
      const selection = this.unsplashSelection;
      if (selection.length) {
        // Track photo downloads per Unsplash's API requirements
        selection.map(photo => {
          this.$http.get('/utilities/unsplash/track-download', { params: { id: photo.id } });
        });
        this.$emit('select', 'unsplash', selection);
        this.close();
      }
    },

    handleTabClick() {
      this.$nextTick(() => {
        if (this.activeTab === 'pages') this.$refs.pageFilter.focus();
        if (this.activeTab === 'unsplash') this.$refs.unsplashFilter.focus();
      });
    },

    handleUpload(event) {
      const files = event.target.files || event.dataTransfer.files;
      const formData = new FormData();
      formData.append('path', this.currentPath);
      [...files].map(file => formData.append('files', file));

      this.isUploading = true;
      this.$refs.fileInput.value = ''; // reset input for subsequent uploads
      this.$http
        .post(`/sites/${this.site.id}/files`, formData)
        .then(res => {
          const uploadedFiles = res.data.files.map(file => {
            return Object.assign(file, { isDisabled: !this.canSelect(file) });
          });
          this.uploads = this.sortFiles(this.uploads.concat(uploadedFiles));
          this.$refs.uploadList.setSelection(res.data.files);
          this.$nextTick(() => this.$refs.uploadList.scrollToSelection());
        })
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => (this.isUploading = false));
    },

    isPageFiltered(page) {
      const filter = this.pageFilter.toLowerCase();
      const name = page.name.toLowerCase();
      const path = page.path.toLowerCase();

      if (filter.length) {
        return name.indexOf(filter) === -1 && path.indexOf(filter) === -1;
      } else {
        return false;
      }
    },

    isUnderBasePath(path) {
      path = path || '';
      return path.substring(0, this.basePath.length) === this.basePath;
    },

    openUpload() {
      const selection = this.uploadSelection[0];

      if (selection) {
        const fileUrl = new URI(this.site.url).path(
          URI.joinPaths(this.site.url, removeBasePath(selection.path, this.site.homeFolder))
        );
        window.open(fileUrl.absoluteTo(this.site.url).toString());
      }
    },

    showCreateFolderForm() {
      this.newFolderName = '';
      this.isCreateFolderFormOpen = true;
      this.$nextTick(() => {
        this.$refs.createFolderInput.focus();
      });
    },

    showRenameForm() {
      const selection = this.uploadSelection;
      if (selection[0]) {
        this.newFilename = selection[0].name;
        this.isRenameFormOpen = true;
        this.$nextTick(() => {
          // Select the filename minus extension
          const lastIndex = this.newFilename.lastIndexOf('.');
          const input = this.$refs.renameInput.$el.querySelector('input');
          input.focus();
          input.setSelectionRange(0, lastIndex);
        });
      }
    },

    showDeleteForm() {
      const selection = this.uploadSelection;
      if (selection[0]) {
        this.isDeleteFormOpen = true;
      }
    },

    sortFiles(files) {
      // Sort by directory, then filename
      return files.sort((a, b) => {
        if (a.type === 'directory' && b.type !== 'directory') {
          return -1;
        } else if (a.type !== 'directory' && b.type === 'directory') {
          return 1;
        } else {
          return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
        }
      });
    }
  }
};
</script>

<template>
  <div
    :class="{
      'file-browser': true,
      'is-inline': type === 'inline',
      'is-dialog': type === 'dialog',
      'is-popover': type === 'popover'
    }"
  >
    <el-tabs v-model="activeTab" @tab-click="handleTabClick">
      <!-- Pages -->
      <el-tab-pane v-if="showPages" :label="$t('Pages')" name="pages" class="pages-tab">
        <el-input
          ref="pageFilter"
          v-model="pageFilter"
          :placeholder="$t('Search Pages')"
          class="page-filter"
          prefix-icon="el-icon-fa-search"
          size="small"
          clearable
        />

        <file-list
          ref="pageList"
          v-model="pages"
          :loading="isLoadingPages"
          :filter-method="isPageFiltered"
          :multiple="multiple"
          @dblclick="handlePageSelect"
          @select="pageSelection = $event"
        />

        <!-- Page options -->
        <div class="page-options">
          <el-button
            v-if="showSelect"
            :disabled="multiple ? pageSelection.length < 1 : pageSelection.length !== 1"
            type="primary"
            @click="handlePageSelect"
          >
            {{ $t('Select') }}
          </el-button>
        </div>
      </el-tab-pane>

      <!-- Uploads -->
      <el-tab-pane :label="$t('Uploads')" name="uploads" class="uploads-tab">
        <!-- Breadcrumbs -->
        <breadcrumbs :path="currentPath" :base-path="basePath" class="breadcrumbs" @path-change="getUploads" />

        <file-list
          ref="uploadList"
          v-model="uploads"
          :allow-drop="true"
          :can-select="canSelect"
          :loading="isLoadingUploads || isCreatingFolder || isDeleting || isRenaming || isUploading"
          :multiple="multiple"
          @drop="handleUpload"
          @dblclick="handleUploadSelect"
          @path-change="getUploads"
          @select="uploadSelection = $event"
        />

        <!-- File options -->
        <div class="upload-options">
          <div class="upload-options-first">
            <el-button-group>
              <!-- Upload -->
              <label :title="$t('Upload')" class="el-button">
                <i class="el-icon-fa-upload" />
                <input ref="fileInput" type="file" multiple @change="handleUpload" />
              </label>

              <!-- Create folder -->
              <el-button :title="$t('New Folder')" @click="showCreateFolderForm">
                <i class="el-icon-fa-folder-plus" />
              </el-button>

              <!-- Rename -->
              <el-button :title="$t('Rename')" :disabled="uploadSelection.length !== 1" @click="showRenameForm">
                <i class="el-icon-fa-tag" />
              </el-button>

              <!-- Open URL -->
              <el-button
                :title="$t('Open in a New Window')"
                :disabled="uploadSelection.length !== 1"
                @click="openUpload"
              >
                <i class="el-icon-fa-external-link" />
              </el-button>

              <!-- Delete -->
              <el-button :title="$t('Delete')" :disabled="uploadSelection.length <= 0" @click="showDeleteForm">
                <i class="el-icon-fa-trash" />
              </el-button>
            </el-button-group>
          </div>

          <div class="upload-options-last">
            <!-- Select -->
            <el-button
              v-if="showSelect"
              :disabled="multiple ? uploadSelection.length < 1 : uploadSelection.length !== 1"
              type="primary"
              @click="handleUploadSelect"
            >
              {{ $t('Select') }}
            </el-button>
          </div>
        </div>
      </el-tab-pane>

      <!-- Unsplash -->
      <el-tab-pane v-if="showUnsplash" label="Unsplash" name="unsplash" class="unsplash-tab">
        <el-row :gutter="20">
          <el-col :span="14">
            <el-input
              ref="unsplashFilter"
              v-model="unsplashFilter"
              :placeholder="$t('Search Unsplash')"
              :prefix-icon="isLoadingUnsplash ? 'el-icon-fa-circle-notch el-icon-fa-spin' : 'el-icon-fa-search'"
              class="unsplash-filter"
              size="small"
              clearable
              @input="getUnsplashImages(true)"
            />
          </el-col>
          <el-col :span="10">
            <el-select
              v-model="unsplashOrientation"
              :placeholder="$t('Orientation')"
              class="unsplash-orientation"
              size="small"
              @input="getUnsplashImages(true)"
            >
              <el-option :label="$t('All Images')" value="" />
              <el-option :label="$t('Portrait')" value="portrait" />
              <el-option :label="$t('Landscape')" value="landscape" />
              <el-option :label="$t('Squarish')" value="squarish" />
            </el-select>
          </el-col>
        </el-row>

        <image-list
          ref="unsplashList"
          v-model="unsplashImages"
          :cols="3"
          :empty-text="$t('Search hundreds of thousands of free, high-quality photos.')"
          :height="280"
          :multiple="multiple"
          @scroll-near-bottom="getUnsplashImages(false)"
          @select="unsplashSelection = $event"
          @dblclick="handleUnsplashSelect"
        />

        <!-- Image search options -->
        <div class="unsplash-options">
          <small class="unsplash-link">
            Powered by
            <a href="https://unsplash.com/?utm_source=Surreal%20CMS&utm_medium=referral" target="_blank" rel="noopener">
              Unsplash
            </a>
          </small>

          <el-button
            v-if="showSelect"
            :disabled="multiple ? unsplashSelection.length < 1 : unsplashSelection.length !== 1"
            type="primary"
            @click="handleUnsplashSelect"
          >
            {{ $t('Select') }}
          </el-button>
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- New folder form -->
    <transition name="fade">
      <el-form v-if="isCreateFolderFormOpen" class="input-form" @submit.native.prevent="handleCreateFolder">
        <div class="input-form-body">
          <el-form-item :label="$t('New Folder')">
            <el-input
              ref="createFolderInput"
              v-model="newFolderName"
              type="text"
              spellcheck="false"
              auto-complete="off"
              @change="handleNewFolderNameChange"
            />
            <div class="text-form-description">
              {{ $t('Enter a name for the new folder.') }}
            </div>
          </el-form-item>
          <div class="text-right">
            <el-button @click="isCreateFolderFormOpen = false">
              {{ $t('Cancel') }}
            </el-button>
            <el-button type="primary" native-type="submit">
              {{ $t('Create Folder') }}
            </el-button>
          </div>
        </div>
      </el-form>
    </transition>

    <!-- Rename form -->
    <transition name="fade">
      <el-form v-if="isRenameFormOpen" class="input-form" @submit.native.prevent="handleRename">
        <div class="input-form-body">
          <el-form-item :label="$t('Rename')">
            <el-input
              ref="renameInput"
              v-model="newFilename"
              type="text"
              spellcheck="false"
              auto-complete="off"
              @change="handleNewFilenameChange"
            />
            <div class="text-form-description">
              {{ $t('This will cause existing links to this file to stop working.') }}
            </div>
          </el-form-item>
          <div class="text-right">
            <el-button @click="isRenameFormOpen = false">
              {{ $t('Cancel') }}
            </el-button>
            <el-button type="primary" native-type="submit">
              {{ $t('Rename') }}
            </el-button>
          </div>
        </div>
      </el-form>
    </transition>

    <!-- Delete form -->
    <transition name="fade">
      <el-form v-if="isDeleteFormOpen" class="input-form" @submit.native.prevent="handleDelete">
        <div class="input-form-body">
          <div class="confirmation">
            <h4>
              {{
                $tc('Are you sure you want to delete {count} files?', uploadSelection.length, {
                  count: uploadSelection.length
                })
              }}
            </h4>
            <p class="text-form-description">
              {{ $t('Files cannot be recovered once they are deleted.') }}
            </p>
          </div>
          <div class="text-right">
            <el-button @click="isDeleteFormOpen = false">
              {{ $t('Cancel') }}
            </el-button>
            <el-button type="danger" native-type="submit">
              {{ $t('Delete') }}
            </el-button>
          </div>
        </div>
      </el-form>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.file-browser {
  position: relative;

  &.is-dialog {
    padding: 0;
    margin: 0;
  }

  &.is-inline {
    margin-bottom: 1.5rem;
  }

  &.is-popover {
    width: 420px;
    padding: 0 0.5rem 0.5rem 0.5rem;

    @include breakpoint('sm-and-down') {
      width: 320px;
    }
  }
}

.breadcrumbs {
  margin-top: -1rem;
}

.uploads-tab {
  .upload-options {
    display: flex;

    .upload-options-first {
      flex: 1 1 auto;
    }

    .upload-options-last {
      flex: 0 0 auto;
      text-align: right;
    }

    @include breakpoint('sm-and-down') {
      display: block;

      .upload-options-first {
        text-align: right;
      }

      .upload-options-last {
        text-align: right;
        margin-top: 0.5rem;
      }
    }

    .el-button-group {
      margin-right: 0.5rem;
    }

    label.el-button {
      height: 41px;
    }

    > .el-button:last-child {
      float: right;
    }

    input[type='file'] {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }
  }
}

.pages-tab {
  .page-filter {
    margin-top: -1rem;
    margin-bottom: 1rem;
  }

  .page-options {
    .el-button:last-child {
      float: right;
    }
  }
}

.unsplash-tab {
  .unsplash-filter,
  .unsplash-orientation {
    margin-top: -1rem;
    margin-bottom: 1rem;
  }

  .unsplash-options {
    .unsplash-link {
      font-size: $--font-size-small;
      color: $--color-gray-40;
    }

    .el-button:last-child {
      float: right;
    }
  }

  /deep/ .no-items {
    font-size: $--font-size-small;
    padding: 4rem;
  }
}

.input-form {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: $--color-white;
  padding: 1rem;

  .input-form-body {
    width: 100%;
  }

  &.fade-enter-active,
  &.fade-leave-active {
    transition: opacity 0.25s, transform 0.25s;
    transform: scale(1);
  }

  &.fade-enter,
  &.fade-leave-to {
    opacity: 0;
    transform: scale(0.25);
  }

  .confirmation {
    margin-bottom: 1.5rem;

    h4 {
      margin-bottom: 0;
    }
  }
}
</style>
